import { CaretDown } from "@phosphor-icons/react"

import { Button } from "@tokenterminal/core/ui/button/Button"
import { Heading } from "@tokenterminal/core/ui/heading/Heading"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"

import { ComboboxWithPinnedItems } from "@tokenterminal/core/ui/combobox/ComboboxWithPinnedItems"
import { toDictionary } from "@tokenterminal/core/utils/toDictionary"

import { type DataGridState, type MartPreset } from "modules/datatable/types"
import { MartDataGrid } from "modules/MartDataGrid"
import { type NAVIGATION_ITEM_TABLE_ENUM } from "modules/project/hooks/useProjectNavigation"
import { Suspense, useEffect, useMemo, useRef, useState } from "react"
import { type MartColumn } from "types/ApiTypes"
import ColumnSelectorModal from "./ColumnSelectorModal/ColumnSelectorModal"
import { datasetTypes } from "./constants"
import { useMart } from "./useMart"
import type {
  GridOptions as AgGridOptions,
  ColumnApi,
} from "@ag-grid-community/core"

function DatasetWithEditableControls({
  selectedMartId,
  filters,
  removeGroupBys = false,
  onStateChange,
  customPreset,
  height,
  title,
  description,
  rightHeaderSlot,
}: {
  selectedMartId: NAVIGATION_ITEM_TABLE_ENUM | null | string
  filters?: {
    project_name?: string
    chain_name?: string
  }
  removeGroupBys?: boolean
  onStateChange?: (state: DataGridState) => void
  customPreset?: MartPreset
  height?: string
  title?: string
  description?: string | null
  rightHeaderSlot?: React.ReactNode
}) {
  const [datasetType, setDatasetType] = useState<
    (typeof datasetTypes)[number]["value"]
  >((selectedMartId as NAVIGATION_ITEM_TABLE_ENUM) || datasetTypes[0].value)
  const apiRef = useRef<AgGridOptions | null>(null)
  const [tableState, setTableState] = useState<DataGridState | null>(null)
  const { data: mart } = useMart(datasetType)

  const columnDefinitionsByColId = toDictionary(
    mart?.column_definitions || [],
    "col_id"
  )

  const preset = mart?.default_preset

  if (filters && preset) {
    Object.keys(filters).forEach((key) => {
      if (preset.config.filters) {
        // @ts-ignore
        preset.config.filters[key] = {
          filterType: "multi",
          filterModels: [
            {
              filterType: "text",
              type: "equals",
              filter: filters[key as keyof typeof filters],
            },
            null,
          ],
        }
      }
    })
  }

  useEffect(() => {
    if (apiRef.current) {
      apiRef.current.columnApi?.autoSizeAllColumns()

      if (removeGroupBys) {
        apiRef.current.columnApi?.setRowGroupColumns([])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRef.current, removeGroupBys, preset])

  const {
    currentGroupByColumns,
    noGroupingColumns,
    availableGroupByColumns,
    defaultGroupByValues,
    groupByValues,
    defaultColumns,
    activeColumns,
    activeColumnsWithoutAutoColumns,
    defaultGroupByIds,
  } = useMemo(() => {
    const currentGroupByColumns =
      tableState?.columns
        .filter((col) => col.rowGroup)
        .sort((a, b) => {
          const aIndex = a.rowGroupIndex
          const bIndex = b.rowGroupIndex
          return (aIndex || 0) - (bIndex || 0)
        })
        .map((col) => col.colId) || []

    const defaultGroupByIds =
      mart?.default_preset?.config.columns
        .filter((col) => col.rowGroup)
        .map((col) => col.colId) || []

    const defaultGroupByValues =
      mart?.default_preset?.config.columns
        // @ts-ignore
        .filter((col) => col.aggFunc)
        .map((col) => col.colId) || []

    const groupByValues =
      tableState?.columns
        .filter((col) => col.aggFunc)
        .map((col) => col.colId) || []

    const defaultColumns =
      mart?.default_preset?.config.columns
        .filter((col) => !col.colId.includes("ag-Grid-AutoColumn-"))
        .filter((col) => !col.hide)
        .map((col) => col.colId) || []

    const activeColumnsWithoutAutoColumns =
      tableState?.columns
        .filter((col) => !col.colId.includes("ag-Grid-AutoColumn-"))
        .filter((col) => !col.hide && !col.pinned)
        .map((col) => col.colId) || []

    const activeColumns =
      tableState?.columns
        .filter((col) => !col.hide && !col.pinned)
        .map((col) => ({
          ...col,
          colId: col.colId.replace("ag-Grid-AutoColumn-", ""),
        })) || []

    const availableGroupByColumns =
      mart?.column_definitions
        .filter((column) =>
          tableState?.columns
            .filter((col) => !col.hide)
            .filter((col) => !col.aggFunc)
            .find((col) => col.colId === column.col_id)
        )
        .filter((column) => !currentGroupByColumns?.includes(column.col_id))
        .filter((column) => column.enable_value_row) || []

    const noGroupingColumns =
      groupByValues.length === 0 && availableGroupByColumns.length === 0

    return {
      currentGroupByColumns,
      availableGroupByColumns,
      noGroupingColumns,
      defaultGroupByValues,
      groupByValues,
      defaultColumns,
      activeColumnsWithoutAutoColumns,
      activeColumns,
      defaultGroupByIds,
    }
  }, [tableState, mart])

  return (
    <div className="flex flex-col md:flex-row gap-10 w-full items-start h-full overflow-hidden">
      <div className="flex flex-1 relative h-full w-full">
        {mart && (
          <div className="flex flex-col gap-6 max-w-full h-[900px] md:h-full w-full">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="flex flex-col gap-1">
                <Heading level={2} as="h1">
                  {title || mart.name}
                </Heading>
                {description !== null && (
                  <p className="text-sm text-fg-secondary">
                    {description || mart.description}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                {!removeGroupBys && (
                  <ComboboxWithPinnedItems
                    drawerHeaderTitle="Grouped by"
                    selectedIds={currentGroupByColumns}
                    onChangeSelectedIds={(ids) => {
                      if (ids && ids.length > 0) {
                        ids?.forEach((id) => {
                          apiRef?.current?.columnApi?.addRowGroupColumn(id)
                          apiRef?.current?.columnApi?.setColumnVisible(
                            id,
                            false
                          )
                        })
                      } else {
                        currentGroupByColumns.forEach((id) => {
                          apiRef?.current?.columnApi?.removeRowGroupColumn(id)
                          apiRef?.current?.columnApi?.setColumnVisible(id, true)
                        })
                      }
                    }}
                    trigger={
                      <Button className="self-start">
                        Grouped by
                        <Icon as={CaretDown} />
                      </Button>
                    }
                    items={mart?.column_definitions
                      .filter((column) => column.enable_row_group)
                      .map((column) => {
                        const parentColumnDefinition =
                          columnDefinitionsByColId.get(column?.parent || "")

                        return {
                          id: column.col_id,
                          value: column.col_id,
                          children: parentColumnDefinition?.header_name
                            ? `${parentColumnDefinition?.header_name} ${column.header_name || column.col_id}`
                            : column.header_name || column.col_id,
                        }
                      })}
                  />
                )}
                <ColumnSelectorModal
                  buttonLabel="Edit columns"
                  columnDefinitions={mart?.column_definitions || []}
                  columnApi={apiRef?.current?.columnApi || ({} as ColumnApi)}
                  selectedColumnIds={activeColumns.map((col) => col.colId)}
                  currentGroupByColumns={currentGroupByColumns}
                />
                {rightHeaderSlot}
              </div>
            </div>
            <Suspense fallback={null}>
              <MartDataGrid
                height={height}
                apiRef={apiRef}
                martId={mart.mart_id}
                defaultPreset={preset as unknown as MartPreset}
                customPreset={customPreset}
                columns={
                  mart?.column_definitions as unknown as Array<MartColumn>
                }
                initialSideBar={null}
                readOnly
                key={mart.mart_id}
                onStateChange={(state) => {
                  setTableState(state)
                  onStateChange?.(state)
                }}
                disableCharts
              />
            </Suspense>
          </div>
        )}
      </div>
    </div>
  )
}

export default DatasetWithEditableControls
